/*
 * Automatic build-version check.
 *
 * The app knows the build id it was compiled with (REACT_APP_GIT_SHA). The
 * server exposes the currently deployed build id in /version.json, generated
 * at build time and never cached. When they differ, browsers holding a stale
 * cached copy of the app are forced to reload so every user gets the latest
 * deployed functionality without pressing F5.
 */

const CURRENT_BUILD_ID = process.env.REACT_APP_GIT_SHA || 'dev';
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_GUARD_KEY = 'kab-version-reload';
const RELOAD_GUARD_TTL_MS = 60 * 1000;

async function fetchDeployedBuildId() {
  const response = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`version.json responded with HTTP ${response.status}`);
  }
  const data = await response.json();
  return typeof data.buildId === 'string' ? data.buildId.trim() : '';
}

function recentlyReloadedFor(buildId) {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (!raw) return false;
    const { id, at } = JSON.parse(raw);
    return id === buildId && Date.now() - at < RELOAD_GUARD_TTL_MS;
  } catch (err) {
    return false;
  }
}

function markReloadFor(buildId) {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify({ id: buildId, at: Date.now() }));
  } catch (err) {
    // sessionStorage unavailable; the TTL guard simply won't apply.
  }
}

async function forceReload(targetBuildId) {
  markReloadFor(targetBuildId);

  // Clear service worker registrations and Cache Storage left behind by old
  // builds so they cannot keep serving stale assets after the reload.
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
    if (window.caches && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch (err) {
    // Best effort: reload anyway.
  }

  window.location.reload();
}

export async function checkForNewVersion() {
  // Local development builds have no meaningful build id to compare.
  if (CURRENT_BUILD_ID === 'dev') return;

  let deployedBuildId;
  try {
    deployedBuildId = await fetchDeployedBuildId();
  } catch (err) {
    return; // Network hiccup or old deploy without version.json; try later.
  }

  if (!deployedBuildId || deployedBuildId === CURRENT_BUILD_ID) return;

  // Guard against reload loops if the server keeps announcing a build we
  // cannot obtain (e.g. an intermediary still caching the old bundle).
  if (recentlyReloadedFor(deployedBuildId)) return;

  forceReload(deployedBuildId);
}

export function startVersionCheck() {
  checkForNewVersion();

  setInterval(checkForNewVersion, CHECK_INTERVAL_MS);

  // Re-check when the user returns to the tab/window, the moment stale
  // versions are most commonly noticed.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForNewVersion();
    }
  });
}
